import { useState, useEffect, useMemo, useCallback } from 'react';
import { getMarketPriceSymbol } from '@/data/instrumentRegistry';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Loader2, X, Plus, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { SoftUpgradePrompt } from '@/components/shared/SoftUpgradePrompt';
import type { TradingState } from '@/pages/TradingZone';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface OrderPanelProps {
  symbol: string;
  currentPrice: number;
  instrumentDecimals: number;
  tradingState?: TradingState;
  brokerName?: string | null;
  demoAccountId?: string | null;
}

type OrderSide = 'buy' | 'sell';
type OrderType = 'market' | 'limit' | 'stop';
type ExecutionMode = 'demo' | 'live';

const PIP_VALUE_STANDARD = 10;

export default function OrderPanel({
  symbol, currentPrice, instrumentDecimals,
  tradingState = 'demo-only', brokerName, demoAccountId,
}: OrderPanelProps) {
  const { user } = useAuth();
  const { tier, limits } = useSubscription();
  const [side, setSide] = useState<OrderSide>('buy');
  const [orderType, setOrderType] = useState<OrderType>('market');
  const [lots, setLots] = useState('0.01');
  const [limitPrice, setLimitPrice] = useState('');
  const [slPips, setSlPips] = useState('');
  const [tpPips, setTpPips] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [positions, setPositions] = useState<any[]>([]);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [demoBalance, setDemoBalance] = useState<number | null>(null);
  const [executionMode, setExecutionMode] = useState<ExecutionMode>('demo');
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [isCreatingDemo, setIsCreatingDemo] = useState(false);

  const pipSize = instrumentDecimals >= 4 ? 0.0001 : instrumentDecimals === 2 ? 0.01 : 0.01;
  const displaySymbol = symbol.includes('/') ? symbol : `${symbol.slice(0, 3)}/${symbol.slice(3)}`;

  const slPrice = useMemo(() => {
    if (!slPips || !currentPrice) return null;
    const pips = parseFloat(slPips);
    return side === 'buy' ? currentPrice - pips * pipSize : currentPrice + pips * pipSize;
  }, [slPips, currentPrice, side, pipSize]);

  const tpPrice = useMemo(() => {
    if (!tpPips || !currentPrice) return null;
    const pips = parseFloat(tpPips);
    return side === 'buy' ? currentPrice + pips * pipSize : currentPrice - pips * pipSize;
  }, [tpPips, currentPrice, side, pipSize]);

  const risk = useMemo(() => {
    if (!slPips || !lots) return null;
    return parseFloat(lots) * PIP_VALUE_STANDARD * parseFloat(slPips);
  }, [lots, slPips]);

  const fetchPositions = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('strategy_positions')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'open')
      .order('opened_at', { ascending: false });
    if (data) setPositions(data);
  }, [user]);

  const fetchDemoBalance = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('demo_accounts')
      .select('balance')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true })
      .limit(1)
      .single();
    if (data) setDemoBalance(Number(data.balance));
  }, [user]);

  useEffect(() => {
    fetchPositions();
    fetchDemoBalance();
  }, [fetchPositions, fetchDemoBalance]);

  useEffect(() => {
    if (!user) return;
    const posChannel = supabase
      .channel('order-panel-positions')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'strategy_positions' }, () => {
        fetchPositions();
      })
      .subscribe();
    const balChannel = supabase
      .channel('order-panel-balance')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'demo_accounts' }, () => {
        fetchDemoBalance();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(posChannel);
      supabase.removeChannel(balChannel);
    };
  }, [user, fetchPositions, fetchDemoBalance]);

  const todayPnl = useMemo(() => {
    return positions.reduce((sum, p) => sum + (p.unrealized_pnl || 0), 0);
  }, [positions]);

  const handleCreateDemo = async () => {
    if (!user) return;
    setIsCreatingDemo(true);
    try {
      const { data, error } = await supabase.functions.invoke('demo-accounts', {
        body: { action: 'create', initial_balance: 10000, account_name: 'Demo Account' },
      });
      if (error) throw error;
      toast({ title: 'Demo account created', description: 'You have $10,000 to practice with.' });
      fetchDemoBalance();
      window.location.reload();
    } catch (e: any) {
      toast({ title: 'Failed to create demo account', description: e.message, variant: 'destructive' });
    } finally {
      setIsCreatingDemo(false);
    }
  };

  const handleModeSwitch = (mode: ExecutionMode) => {
    if (mode === 'live') {
      if (tradingState !== 'broker-connected') {
        toast({ title: 'No broker connected', description: 'Connect a broker first to trade live.', variant: 'destructive' });
        return;
      }
      if (!limits.canLive) {
        setShowUpgradePrompt(true);
        return;
      }
    }
    setExecutionMode(mode);
  };

  const handlePlaceOrder = async () => {
    if (!user) {
      toast({ title: 'Please sign in', description: 'You need to be logged in to place orders.', variant: 'destructive' });
      return;
    }
    if (executionMode === 'live') {
      setShowLiveConfirm(true);
      return;
    }
    await executePlaceOrder();
  };

  const executePlaceOrder = async () => {
    setIsSubmitting(true);
    try {
      const isLive = executionMode === 'live';
      const fnName = isLive ? 'broker-execute' : 'place-manual-order';

      const { data, error } = await supabase.functions.invoke(fnName, {
        body: {
          symbol: getMarketPriceSymbol(symbol),
          side,
          quantity: parseFloat(lots),
          order_type: orderType,
          ...(orderType !== 'market' && limitPrice ? { price: parseFloat(limitPrice) } : {}),
          ...(isLive && brokerName ? { broker_name: brokerName } : {}),
        },
      });

      if (error) throw error;
      if (data && !data.success) throw new Error(data.error || 'Order failed');

      const modeLabel = isLive ? 'Live' : 'Demo';
      toast({ title: `${modeLabel} order placed`, description: `${side.toUpperCase()} ${lots} lots ${displaySymbol} @ $${data?.filled_price?.toFixed(2) ?? ''}` });

      fetchPositions();
      fetchDemoBalance();
    } catch (e: any) {
      toast({ title: 'Order failed', description: e.message || 'Unknown error', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePosition = async (posId: string) => {
    const pos = positions.find(p => p.id === posId);
    if (!pos) {
      toast({ title: 'Position not found', variant: 'destructive' });
      return;
    }

    setClosingId(posId);
    try {
      // strategy_positions.side is 'long' | 'short'; broker side is 'buy' | 'sell'.
      const closeSide = pos.side === 'long' ? 'sell' : 'buy';
      const fnName = executionMode === 'live' ? 'broker-execute' : 'place-manual-order';

      const { error: orderErr } = await supabase.functions.invoke(fnName, {
        body: {
          symbol: pos.symbol,
          side: closeSide,
          quantity: pos.quantity,
          order_type: 'market',
        },
      });
      if (orderErr) {
        const msg = (orderErr as any)?.message || 'Broker rejected the close order';
        toast({ title: 'Failed to close position', description: msg, variant: 'destructive' });
        return;
      }

      const { error } = await supabase
        .from('strategy_positions')
        .update({ status: 'closed', closed_at: new Date().toISOString() })
        .eq('id', posId);
      if (error) throw error;

      setPositions(prev => prev.filter(p => p.id !== posId));
      toast({ title: 'Position closed' });
      fetchDemoBalance();
    } catch (e: any) {
      toast({ title: 'Failed to close', description: e.message, variant: 'destructive' });
    } finally {
      setClosingId(null);
    }
  };

  const orderTypes: { value: OrderType; label: string }[] = [
    { value: 'market', label: 'Market' },
    { value: 'limit', label: 'Limit' },
    { value: 'stop', label: 'Stop' },
  ];

  const balanceDisplay = demoBalance !== null ? `$${demoBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

  // === NO DEMO STATE ===
  if (tradingState === 'no-demo') {
    return (
      <div className="flex flex-col h-full items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-accent-green/10 flex items-center justify-center mb-4">
          <Plus className="w-8 h-8 text-accent-green" />
        </div>
        <h3 className="text-sm font-semibold text-foreground mb-2">Start Paper Trading</h3>
        <p className="text-xs text-muted-foreground mb-6 max-w-[200px]">
          Create a demo account with $10,000 virtual funds to practice risk-free.
        </p>
        <button
          onClick={handleCreateDemo}
          disabled={isCreatingDemo}
          className="px-6 py-2.5 rounded-lg bg-accent-green text-foreground text-xs font-bold flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {isCreatingDemo && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Create Demo Account
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full text-foreground/80">
      {/* LIVE CONFIRM DIALOG */}
      <AlertDialog open={showLiveConfirm} onOpenChange={setShowLiveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-accent-amber" />
              Confirm Live Order
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will place a <span className="font-semibold text-foreground">REAL order</span> through{' '}
              <span className="font-semibold text-foreground">{brokerName}</span>.{' '}
              {side.toUpperCase()} {lots} lots of {displaySymbol}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setShowLiveConfirm(false); executePlaceOrder(); }}
              className={side === 'buy' ? 'bg-accent-green hover:bg-accent-green/90' : 'bg-accent-red hover:bg-accent-red/90'}
            >
              Confirm {side.toUpperCase()} Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* UPGRADE PROMPT */}
      {showUpgradePrompt && (
        <div className="p-3 border-b border-border">
          <SoftUpgradePrompt feature="live_trading" message="Upgrade to Pro to access live trading with real brokers." />
          <button onClick={() => setShowUpgradePrompt(false)} className="mt-2 text-[10px] text-muted-foreground hover:text-foreground">
            Dismiss
          </button>
        </div>
      )}

      {/* DEMO/LIVE TOGGLE */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex rounded-lg overflow-hidden border border-border flex-1">
            <button
              onClick={() => handleModeSwitch('demo')}
              className={`flex-1 py-1.5 text-[10px] font-bold tracking-wide transition-colors flex items-center justify-center gap-1 ${
                executionMode === 'demo'
                  ? 'bg-accent-green/15 text-accent-green'
                  : 'bg-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <ShieldCheck className="w-3 h-3" />
              DEMO
            </button>
            <button
              onClick={() => handleModeSwitch('live')}
              className={`flex-1 py-1.5 text-[10px] font-bold tracking-wide transition-colors flex items-center justify-center gap-1 ${
                executionMode === 'live'
                  ? 'bg-accent-amber/15 text-accent-amber'
                  : 'bg-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <AlertTriangle className="w-3 h-3" />
              LIVE
            </button>
          </div>
        </div>
        {executionMode === 'live' && brokerName && (
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-1.5 h-1.5 rounded-full bg-accent-amber" />
            <span className="text-[10px] font-medium text-accent-amber capitalize">{brokerName}</span>
          </div>
        )}
      </div>

      {/* ORDER SECTION */}
      <div className="p-3 border-b border-border">
        <div className="text-[10px] font-bold tracking-widest text-muted-foreground mb-2 font-heading">ORDER</div>

        {/* Buy/Sell Toggle */}
        <div className="grid grid-cols-2 gap-1 mb-3">
          <button
            onClick={() => setSide('buy')}
            className={`py-2 rounded text-xs font-bold transition-colors font-heading ${
              side === 'buy'
                ? 'bg-accent-green text-foreground'
                : 'bg-transparent text-accent-green border border-border'
            }`}
          >
            BUY
          </button>
          <button
            onClick={() => setSide('sell')}
            className={`py-2 rounded text-xs font-bold transition-colors font-heading ${
              side === 'sell'
                ? 'bg-accent-red text-foreground'
                : 'bg-transparent text-accent-red border border-border'
            }`}
          >
            SELL
          </button>
        </div>

        {/* Order Type Tabs */}
        <div className="flex gap-0.5 mb-3 p-0.5 rounded bg-background">
          {orderTypes.map(ot => (
            <button
              key={ot.value}
              onClick={() => setOrderType(ot.value)}
              className={`flex-1 py-1 text-[10px] font-semibold rounded transition-colors ${
                orderType === ot.value
                  ? 'bg-secondary text-foreground'
                  : 'bg-transparent text-muted-foreground'
              }`}
            >
              {ot.label}
            </button>
          ))}
        </div>

        {/* Inputs */}
        <div className="space-y-2">
          {orderType !== 'market' && (
            <div>
              <label className="text-[10px] text-muted-foreground mb-0.5 block">Price</label>
              <Input
                type="number"
                value={limitPrice}
                onChange={e => setLimitPrice(e.target.value)}
                className="h-8 text-xs bg-transparent border-border text-foreground font-mono-data"
                placeholder={currentPrice?.toFixed(instrumentDecimals) || ''}
                step={pipSize}
              />
            </div>
          )}
          <div>
            <label className="text-[10px] text-muted-foreground mb-0.5 block">Lots</label>
            <Input
              type="number"
              value={lots}
              onChange={e => setLots(e.target.value)}
              className="h-8 text-xs bg-transparent border-border text-foreground font-mono-data"
              step="0.01"
              min="0.01"
            />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground mb-0.5 block">Stop Loss (pips)</label>
            <Input
              type="number"
              value={slPips}
              onChange={e => setSlPips(e.target.value)}
              className="h-8 text-xs bg-transparent border-border text-foreground font-mono-data"
              placeholder="Optional"
            />
            {slPrice !== null && (
              <div className="text-[9px] text-muted-foreground mt-0.5 font-mono-data">
                = {slPrice.toFixed(instrumentDecimals)}
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground mb-0.5 block">Take Profit (pips)</label>
            <Input
              type="number"
              value={tpPips}
              onChange={e => setTpPips(e.target.value)}
              className="h-8 text-xs bg-transparent border-border text-foreground font-mono-data"
              placeholder="Optional"
            />
            {tpPrice !== null && (
              <div className="text-[9px] text-muted-foreground mt-0.5 font-mono-data">
                = {tpPrice.toFixed(instrumentDecimals)}
              </div>
            )}
          </div>

          {risk !== null && risk > 0 && (
            <div className="text-[10px] text-accent-amber font-mono-data">
              Risk: ${risk.toFixed(2)}
            </div>
          )}

          <button
            onClick={handlePlaceOrder}
            disabled={isSubmitting}
            className={`w-full py-2.5 rounded text-xs font-bold text-foreground transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
              side === 'buy' ? 'bg-accent-green' : 'bg-accent-red'
            }`}
          >
            {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {side === 'buy' ? 'BUY' : 'SELL'} ({executionMode === 'live' ? 'Live' : 'Demo'}) {displaySymbol}
          </button>
        </div>
      </div>

      {/* OPEN POSITIONS */}
      <div className="p-3 flex-1 overflow-y-auto border-b border-border">
        <div className="text-[10px] font-bold tracking-widest text-muted-foreground mb-2 font-heading">
          OPEN POSITIONS ({positions.length})
        </div>
        {positions.length === 0 ? (
          <div className="text-[11px] text-muted-foreground/50 py-4 text-center">No open positions</div>
        ) : (
          <div className="space-y-2">
            {positions.map(pos => {
              const isLong = pos.side === 'buy' || pos.side === 'long';
              return (
                <div
                  key={pos.id}
                  className="p-2 rounded text-[11px] bg-background border border-border"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-foreground">{pos.symbol}</span>
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                          isLong
                            ? 'bg-accent-green/15 text-accent-green'
                            : 'bg-accent-red/15 text-accent-red'
                        }`}
                      >
                        {isLong ? 'LONG' : 'SHORT'}
                      </span>
                      <button
                        onClick={() => handleClosePosition(pos.id)}
                        disabled={closingId === pos.id}
                        className="p-0.5 rounded hover:bg-muted/30 transition-colors"
                      >
                        {closingId === pos.id ? (
                          <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                        ) : (
                          <X className="w-3 h-3 text-muted-foreground" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-muted-foreground font-mono-data">
                    <span>{Number(pos.average_entry_price).toFixed(instrumentDecimals)}</span>
                    <span
                      className={`font-semibold ${
                        pos.unrealized_pnl >= 0 ? 'text-profit' : 'text-loss'
                      }`}
                    >
                      {pos.unrealized_pnl >= 0 ? '+' : ''}${Number(pos.unrealized_pnl).toFixed(2)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ACCOUNT SUMMARY */}
      <div className="p-3 mt-auto">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[10px] font-bold tracking-widest text-muted-foreground font-heading">ACCOUNT</div>
          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
            executionMode === 'live'
              ? 'bg-accent-amber/15 text-accent-amber'
              : 'bg-accent-green/15 text-accent-green'
          }`}>
            {executionMode === 'live' ? 'LIVE' : 'DEMO'}
          </span>
        </div>
        <div className="space-y-1.5">
          {[
            [executionMode === 'live' ? 'Broker Balance' : 'Demo Balance', balanceDisplay],
            ['P&L Today', `${todayPnl >= 0 ? '+' : ''}$${Math.abs(todayPnl).toFixed(2)}`],
            ['Open Positions', String(positions.length)],
          ].map(([label, value]) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">{label}</span>
              <span
                className={`text-[11px] font-mono-data ${
                  label === 'P&L Today'
                    ? todayPnl >= 0
                      ? 'text-profit'
                      : 'text-loss'
                    : 'text-foreground/80'
                }`}
              >
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
